const pad2 = (value: number) => String(value).padStart(2, '0');
const pad3 = (value: number) => String(value).padStart(3, '0');

// Locale-safe (ASCII digits) formatting for API params.
export const formatLocalDateParam = (date: Date) => {
  const y = date.getFullYear();
  const m = pad2(date.getMonth() + 1);
  const d = pad2(date.getDate());
  return `${y}-${m}-${d}`;
};

export const formatLocalDateTimeParam = (date: Date) => {
  const y = date.getFullYear();
  const m = pad2(date.getMonth() + 1);
  const d = pad2(date.getDate());
  const hh = pad2(date.getHours());
  const mm = pad2(date.getMinutes());
  const ss = pad2(date.getSeconds());
  const ms = pad3(date.getMilliseconds());
  return `${y}-${m}-${d}T${hh}:${mm}:${ss}.${ms}`;
};

// The API serialises UTC timestamps WITHOUT a trailing Z: EF returns DateTime with Kind=Unspecified,
// and `new Date('2026-08-31T13:47:00')` reads a bare timestamp as LOCAL time. Every inbound timestamp
// must come through here or it renders off by exactly the viewer's UTC offset.
export const parseApiUtc = (value?: string | null): Date | null => {
  if (!value) return null;
  const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);
  const parsed = new Date(hasZone ? value : `${value}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// WALL-CLOCK timestamps (tournament kickoffs — `Match.ScheduledDateTime`): the value IS the company's
// local time and must never be shifted by the viewer's UTC offset ("don't show UTC on the UI, just
// local time"). A stray Z/offset suffix is stripped so the stored hour survives regardless of how a
// producer tagged it. Pair with `wallClockDateParam` / `wallClockTimeParam` when writing a picker
// back — never `toISOString()`.

/** Calendar fields of a wall-clock timestamp — no `Date`, so no timezone can touch them. */
export interface WallClockParts {
  year: number;
  month: number; // 1-12
  day: number;
  hours: number;
  minutes: number;
  seconds: number;
  milliseconds: number;
}

const WALL_CLOCK_RE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d{1,7}))?)?$/;

/**
 * Split a wall-clock timestamp into its calendar fields WITHOUT constructing a `Date`.
 *
 * `new Date('2026-03-08T02:30:00')` is not lossless: in a viewer's spring-forward DST gap that wall
 * time does not exist, so the browser silently rolls it forward (America/New_York renders 03:30) and
 * an edit form would then persist the shifted hour back to the server. Reading the fields textually
 * makes parse → format a stable round-trip in every zone.
 */
export const parseApiWallClockParts = (value?: string | null): WallClockParts | null => {
  if (!value) return null;
  const bare = value.trim().replace(/([zZ]|[+-]\d{2}:?\d{2})$/, '');
  const m = WALL_CLOCK_RE.exec(bare);
  if (!m) return null;

  const [, y, mo, d, hh, mm, ss, frac] = m;
  const parts: WallClockParts = {
    year: Number(y),
    month: Number(mo),
    day: Number(d),
    hours: Number(hh ?? '0'),
    minutes: Number(mm ?? '0'),
    seconds: Number(ss ?? '0'),
    milliseconds: frac ? Number(frac.slice(0, 3).padEnd(3, '0')) : 0,
  };

  const valid =
    parts.month >= 1 && parts.month <= 12 &&
    parts.day >= 1 && parts.day <= 31 &&
    parts.hours <= 23 && parts.minutes <= 59 && parts.seconds <= 59;
  return valid ? parts : null;
};

export const parseApiWallClock = (value?: string | null): Date | null => {
  if (!value) return null;
  const parts = parseApiWallClockParts(value);
  if (parts) {
    // Field-wise construction — never `new Date(string)`, whose bare-string branch is what the DST
    // gap corrupts. (A gap wall time still cannot be *represented* as a local Date; the param
    // helpers below therefore read the parts directly and never round-trip through this Date.)
    const d = new Date(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hours,
      parts.minutes,
      parts.seconds,
      parts.milliseconds,
    );
    if (parts.year < 100) d.setFullYear(parts.year); // 0-99 would map to 1900+y
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const bare = value.replace(/([zZ]|[+-]\d{2}:?\d{2})$/, '');
  const parsed = new Date(bare);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** `HH:mm` of a wall-clock timestamp, for `<input type="time">`. */
export const wallClockTimeParam = (value?: string | null): string => {
  const parts = parseApiWallClockParts(value);
  if (parts) return `${pad2(parts.hours)}:${pad2(parts.minutes)}`;
  const d = parseApiWallClock(value);
  return d ? `${pad2(d.getHours())}:${pad2(d.getMinutes())}` : '';
};

/** `YYYY-MM-DD` of a wall-clock timestamp, for `<input type="date">` (NOT toISOString — no UTC shift). */
export const wallClockDateParam = (value?: string | null): string => {
  const parts = parseApiWallClockParts(value);
  if (parts) return `${String(parts.year).padStart(4, '0')}-${pad2(parts.month)}-${pad2(parts.day)}`;
  const d = parseApiWallClock(value);
  return d ? formatLocalDateParam(d) : '';
};
